// DEPENDENCIES
import { FormikValues } from "formik";

// TYPES
import { Template, PersonType, DraftUUID, ComponentStep } from "../../types";
import { AppDispatch, RootState } from "../../app/store";

// UTILS
import { checkboxRequired, checkbox } from "../../validations";
import { getDraftStepsToSend, handleSendStep } from "../../app/utils";
import { onUpdateFormData } from "../../reducers/onboardingDataReducer";
import {
  isAgrofinanzasOnboarding,
  physicalEmailVerificationAuthorize,
  physicalEmailVerificationSuccess,
} from "../../utils";
import getTemplateData from "../../hooks/useTemplate";
import { getCompanyData, getPartnership } from "../../utils/companies";

// STEPS
import infoStep from "../../steps/physical/infoStep";
import identityStep from "../../steps/physical/identityStep";
import basicInformationStep from "../../steps/physical/basicInformationStep";
import preconfirmationSteps from "../../steps/physical/preconfirmationSteps";
import employmentProfileStep from "../../steps/physical/employmentProfileStep";
import investmentProfileStep from "../../steps/physical/investmentProfileStep";
import personalInformationStep from "../../steps/physical/personalInformationStep";
import emailValidationStep from "../../steps/physical/emailValidationStep";

import bodyIcon from "../../assets/images/icons/ui/body.svg";

const onboardingPhysical = async (
  draftUUID: DraftUUID,
  dispatch: AppDispatch,
  formValues?: FormikValues | null,
): Promise<Template> => {
  try {
    const {
      favicon,
      formTitle,
      logo,
      questions,
      heritageDocuments,
      countries,
      countriesOptions,
      citizenshipOptions,
      activitiesOptions,
      getProvincesFilteredByCountry,
      provinces,
    } = await getTemplateData({
      draftUUID,
      formValues,
    });

    if (heritageDocuments?.length) {
      dispatch(
        onUpdateFormData({
          heritageFiles: heritageDocuments,
        }),
      );
    }

    const { termsAndConditions } = getCompanyData(formValues);

    const physicalSteps = [
      basicInformationStep(),
      emailValidationStep({ step: 2 }),
      identityStep(countries),
      personalInformationStep(
        countriesOptions,
        citizenshipOptions,
        getProvincesFilteredByCountry,
        provinces,
      ),
      employmentProfileStep(
        activitiesOptions,
        countriesOptions,
        getProvincesFilteredByCountry,
        provinces,
      ),
      investmentProfileStep(questions),
    ];

    const partnership = getPartnership(formValues);
    const partnershipSteps = partnership?.onboardingSteps?.PHYSICAL;

    const steps = partnershipSteps
      ? partnershipSteps.map((step) =>
          physicalSteps.find(({ label }) => label === step),
        )
      : physicalSteps;

    return {
      onboardingType: "PHYSICAL",
      formTitle,
      logo,
      icon: bodyIcon,
      favicon,
      steps: [
        ...steps.map((step, index) => ({ step: index + 1, ...step })),
        infoStep({}),
      ],
      onLoad: async ({ draftUUID, dispatch, formik, state }) => {
        if (!draftUUID) return;

        await physicalEmailVerificationAuthorize(
          draftUUID,
          dispatch,
          formik,
          state,
        );
        await physicalEmailVerificationSuccess(dispatch, formik, 2);
      },
      remoteSteps: [
        {
          link: "/dni",
          step: identityStep(countries).subSteps?.find(
            ({ label }) => label === "uploadDNI",
          ) as ComponentStep,
        },
        {
          link: "/identidad",
          step: identityStep(countries).subSteps?.find(
            ({ label }) => label === "livenessValidation",
          ) as ComponentStep,
        },
      ],
      preconfirmation: () => ({
        steps: preconfirmationSteps(
          countriesOptions,
          citizenshipOptions,
          activitiesOptions,
          getProvincesFilteredByCountry,
          provinces,
          questions,
        ),
        checkboxes: termsAndConditions.map((term) => ({
          ...term,
          initialValue: false,
          validation: term.required ? checkboxRequired : checkbox,
        })),
        sendTermsAndConditions: (state, values) =>
          handleSendTermsAndConditions(state, values, dispatch),
      }),
    };
  } catch (error) {
    throw new Error(error as string);
  }
};

const handleSendTermsAndConditions = async (
  state: RootState,
  values: FormikValues,
  dispatch: AppDispatch,
) => {
  const {
    onboardingData: { sendedSteps, formValues },
    activeTemplate: { onboardingType },
  } = state;

  const termsAndConditionsStep = getPreconfirmationStep(values);
  await handleSendStep({
    dispatch,
    personType: onboardingType as PersonType,
    email: formValues?.email,
    sendedSteps,
    stepId: 5,
    step: termsAndConditionsStep,
  });

  const coownersOnboardingStep = {
    "@type": "6_PHYSICAL",
    coOwnerSteps: [],
  };

  await handleSendStep({
    personType: onboardingType as PersonType,
    email: formValues?.email,
    sendedSteps: getDraftStepsToSend(sendedSteps, termsAndConditionsStep),
    stepId: 6,
    step: coownersOnboardingStep,
    dispatch,
  });
};

const getPreconfirmationStep = (values: FormikValues) => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const step: any = {
    "@type": "5_PHYSICAL",
    acceptedOptionalTermsAndConditions: values?.senebiAndExteriorNotes,
    acceptedTermsAndConditions: values?.termsAndConditions,
  };

  if (isAgrofinanzasOnboarding()) {
    step.wantsAllariaAccount = !!values?.wantsAllariaAccount;
  }

  return step;
};

export default onboardingPhysical;
